import React, { useState } from 'react';
import { 
  TrendingUp, 
  ShieldCheck,
  Shield,
  Navigation,
  Instagram,
  Box,
  Construction,
  HardHat,
  BarChart3,
  MapPin,
  ExternalLink,
  Zap,
  QrCode,
  Smartphone,
  Map,
  TrainFront,
  Bike,
  Globe,
  Clapperboard,
  BookOpen,
  Timer,
  PlayCircle,
  Building2,
  Users2,
  Users,
  Star,
  Briefcase,
  Video,
  Camera,
  Award,
  Brain,
  ChevronRight,
  Eye,
  Leaf,
  CheckCircle2,
  Layout,
  Scissors,
  Image,
  Bot,
  Headphones,
  Filter,
  Cpu,
  Magnet,
  Mail,
  Share2,
  Wallet,
  Search
} from 'lucide-react';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  Cell 
} from 'recharts';

const App = () => {
  const [activeTab, setActiveTab] = useState('change');

  // Unified Brand Palette from Logo (Wellington Developments Gold)
  const colors = {
    gold: '#e6c07b',
    goldDark: '#b8924a',
    goldLight: '#f4dfb8',
    charcoal: '#0a0a0a',
    black: '#050505',
    white: '#ffffff'
  };

  const marketVibe = [
    { year: '2023', gambling: 40, realLogic: 10 },
    { year: '2024', gambling: 55, realLogic: 15 },
    { year: '2025', gambling: 30, realLogic: 45 },
    { year: '2026', gambling: 12, realLogic: 75 }, 
  ];

  const profitData = [
    { type: 'Secondary Market', cash: 6, color: '#333' },
    { type: 'Off-Plan Premium', cash: 8.5, color: colors.goldDark },
    { type: 'Metro Connected', cash: 11, color: colors.gold },
    { type: 'Verified Index', cash: 9.2, color: colors.goldLight },
  ];

  const BrandLogo = () => (
    <div className="flex flex-col items-center">
      <div className="relative">
        <div className="absolute inset-0 bg-[#e6c07b] blur-2xl opacity-20 rounded-full"></div>
        <svg width="60" height="60" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative z-10">
          <path d="M20 40L80 160L100 120L120 160L180 40" stroke={colors.gold} strokeWidth="15" strokeLinecap="square" />
          <path d="M85 40L100 70L115 40" stroke={colors.gold} strokeWidth="15" />
          <path d="M85 175L100 195L115 175" fill={colors.gold} />
        </svg>
      </div>
      <div className="mt-2 text-center">
        <div className="text-sm font-bold tracking-[0.3em] text-white">WELLINGTON</div>
        <div className="text-[8px] font-light tracking-[0.5em] text-[#e6c07b]">DEVELOPMENTS</div>
      </div>
    </div>
  );

  const SidebarButton = ({ id, label, icon: Icon, color }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`w-full flex items-center justify-between px-6 py-5 transition-all duration-500 group relative ${
        activeTab === id 
        ? 'bg-[#e6c07b]/10 text-white' 
        : 'text-gray-500 hover:text-white hover:bg-white/5'
      }`}
    >
      <div className="flex items-center gap-4">
        <Icon size={20} style={{ color: activeTab === id ? color : 'inherit' }} className="transition-transform group-hover:scale-110" />
        <span className={`font-bold uppercase tracking-tighter text-[11px] text-left leading-none ${activeTab === id ? 'text-[#e6c07b]' : ''}`}>{label}</span>
      </div>
      {activeTab === id && (
        <div className="absolute left-0 w-1.5 h-full" style={{ backgroundColor: color, boxShadow: `0 0 15px ${color}` }} />
      )}
      <ChevronRight size={14} className={`opacity-0 group-hover:opacity-100 transition-opacity ${activeTab === id ? 'opacity-30' : ''}`} />
    </button>
  );

  const FlipCard = ({ front, back, icon: Icon, color, title }) => {
    return (
      <div className="group h-44 [perspective:1000px]">
        <div className="relative h-full w-full transition-all duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-3xl border border-[#e6c07b]/30 bg-[#e6c07b]/5 p-6 [backface-visibility:hidden]">
            <Icon size={40} style={{ color }} className="mb-4" />
            <h4 className="text-sm font-black uppercase tracking-widest text-white mb-2 text-center">{title}</h4>
            <p className="text-[10px] font-bold uppercase tracking-tighter text-[#e6c07b] text-center">{front}</p>
            <div className="mt-6 flex items-center gap-2 opacity-40">
               <Eye size={12} className="text-[#e6c07b]" />
               <span className="text-[8px] uppercase font-mono tracking-tighter text-[#e6c07b] font-black">Flip Strategy</span>
            </div>
          </div>
          <div className="absolute inset-0 h-full w-full rounded-3xl bg-[#1a1a1a] p-8 text-center [backface-visibility:hidden] [transform:rotateY(180deg)] flex items-center justify-center border border-[#e6c07b]/60 shadow-2xl">
            <p className="text-[11px] font-bold leading-relaxed italic text-[#e6c07b]">{back}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-[#050505] text-white font-sans overflow-hidden relative">
      {/* GRAIN EFFECT */}
      <div className="fixed inset-0 pointer-events-none z-[100] opacity-[0.06]" 
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }}
      />

      <aside className="w-72 border-r border-[#e6c07b]/20 bg-black flex flex-col sticky top-0 h-screen shrink-0 z-20 shadow-2xl">
        <div className="py-12 border-b border-[#e6c07b]/10">
          <BrandLogo />
        </div>
        <nav className="flex-1 overflow-y-auto no-scrollbar pt-4">
          <SidebarButton id="change" label="The Big Change" icon={TrendingUp} color={colors.gold} />
          <SidebarButton id="social" label="Social Stars" icon={Instagram} color={colors.gold} />
          <SidebarButton id="deliverables" label="Deliverables" icon={Box} color={colors.gold} />
          <SidebarButton id="services" label="Team Services" icon={Briefcase} color={colors.gold} />
        </nav>
        <div className="p-8 border-t border-[#e6c07b]/10">
          <div className="flex items-center gap-3 bg-[#e6c07b]/10 p-3 rounded-xl border border-[#e6c07b]/30">
            <div className="w-2 h-2 rounded-full bg-[#e6c07b] animate-pulse shadow-[0_0_10px_#e6c07b]" />
            <span className="text-[9px] font-mono text-[#e6c07b] uppercase tracking-widest font-black">Dopameme Systems</span>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto bg-[#080808]">
        <div className="p-12 pb-4">
          <h2 className="text-[9vw] font-black italic tracking-tighter leading-[0.75] uppercase text-white drop-shadow-[0_10px_20px_rgba(230,192,123,0.15)]">
            DUBAI REAL ESTATE <span className="text-[#e6c07b]">2026</span>
          </h2>
          <div className="flex items-center gap-6 mt-12 mb-6">
            <div className="h-1 w-32 bg-[#e6c07b] shadow-[0_0_20px_rgba(230,192,123,0.6)]" />
            <p className="text-xs font-mono text-[#e6c07b] uppercase tracking-[0.6em] font-black">GROWN UP MARKET</p>
          </div>
        </div>

        <div className="px-12 pb-24 space-y-16 max-w-7xl">
          {activeTab === 'change' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 space-y-12">
              <section className="bg-[#e6c07b]/[0.02] border border-[#e6c07b]/10 p-10 rounded-[3rem] backdrop-blur-xl relative group">
                <div className="absolute top-10 right-10 text-[#e6c07b]/5 group-hover:text-[#e6c07b]/10 transition-colors">
                  <TrendingUp size={180} />
                </div>
                <div className="relative z-10 max-w-2xl">
                  <h3 className="text-4xl font-black italic text-[#e6c07b] uppercase mb-6 tracking-tighter">Hype is Dead. Logic is King.</h3>
                  <p className="text-gray-400 text-lg leading-relaxed mb-10 font-medium">
                    In 2023, it was all about rushing. In 2026, buyers don't flip for quick cash. 
                    Maturity means <span className="text-white font-bold italic underline decoration-[#e6c07b] underline-offset-8 decoration-2">correct decisions</span> replace speculative gambles. 
                    Investors today want to understand exactly what they are buying and why an area is valuable.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-12">
                    <div className="h-[280px] bg-black/40 p-8 rounded-3xl border border-[#e6c07b]/20">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={marketVibe}>
                          <defs>
                            <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={colors.gold} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={colors.gold} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e6c07b10" vertical={false} />
                          <XAxis dataKey="year" stroke="#444" fontSize={10} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{backgroundColor: '#000', borderRadius: '12px', border: '1px solid #e6c07b44'}} />
                          <Area type="monotone" dataKey="gambling" stroke="#444" fill="#333" fillOpacity={0.05} strokeWidth={2} name="Rush/Hype" strokeDasharray="5 5" />
                          <Area type="monotone" dataKey="realLogic" stroke={colors.gold} fill="url(#goldGradient)" strokeWidth={4} name="Real Facts" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-[#e6c07b]/[0.05] p-8 rounded-3xl border border-[#e6c07b]/20">
                        <h4 className="font-black text-[10px] uppercase text-[#e6c07b] mb-6 flex items-center gap-2 tracking-[0.2em] font-black">
                           <Instagram size={14} /> The Instagram Shift
                        </h4>
                        <div className="space-y-6">
                          <div className="flex items-center gap-4 opacity-30 text-gray-500">
                            <div className="w-10 h-10 rounded-full border border-gray-500 flex items-center justify-center text-[10px] line-through italic font-bold">2023</div>
                            <div className="text-[11px] font-bold line-through">"Look at this Luxury!"</div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-[#e6c07b] text-black flex items-center justify-center text-[10px] font-black italic shadow-[0_0_20px_rgba(230,192,123,0.6)]">2026</div>
                            <div className="text-[11px] font-black tracking-wider uppercase text-[#e6c07b]">"The Proof + The Logic"</div>
                          </div>
                          <p className="text-[10px] text-gray-400 leading-relaxed italic border-t border-[#e6c07b]/20 pt-6 font-medium">
                            Winners today answer the "Why" and the "How" before the buyer even asks.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <div className="space-y-8">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 bg-[#e6c07b]/10 border border-[#e6c07b]/30 rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(230,192,123,0.1)]">
                    <Brain className="text-[#e6c07b]" size={32} />
                  </div>
                  <div>
                    <h3 className="text-4xl font-black uppercase italic tracking-tighter">Buyer Psychology 2026</h3>
                    <p className="text-[#e6c07b] text-[11px] uppercase tracking-widest font-mono font-black">The selective analyst mindset.</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
                  <FlipCard front="The Builder" back="Did they finish on time? What is their past quality record and reliability?" icon={Construction} color={colors.gold} title="Track Record" />
                  <FlipCard front="Progress" back="Real construction on site versus beautiful computer renders." icon={HardHat} color={colors.gold} title="Status" />
                  <FlipCard front="Money Plan" back="Exactly what you pay now vs. what is due on handover." icon={BarChart3} color={colors.gold} title="Payment" />
                  <FlipCard front="Area Value" back="Metro access, schools, and sustainable local job demand." icon={MapPin} color={colors.gold} title="Fundamentals" />
                  <FlipCard front="The Exit" back="Can I rent it fast? Can I sell it without long market waits?" icon={ExternalLink} color={colors.gold} title="Strategy" />
                  <FlipCard front="True Yield" back="Real net profit after maintenance and all hidden fees." icon={Zap} color={colors.gold} title="ROI Logic" />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'social' && (
            <div className="animate-in fade-in duration-700 space-y-24 pb-24">
              <div className="space-y-10">
                <div className="flex items-center gap-6">
                   <div className="w-16 h-16 bg-[#e6c07b]/10 border border-[#e6c07b]/30 rounded-full flex items-center justify-center">
                     <Users className="text-[#e6c07b]" size={32} />
                   </div>
                   <div>
                     <h3 className="text-5xl font-black uppercase italic tracking-tighter">Category A: Influencers</h3>
                     <p className="text-[#e6c07b] text-[11px] uppercase tracking-[0.4em] font-mono font-black">Human Personal Brands & Agents</p>
                   </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                  {[
                    { 
                      name: 'Arman_Dubai_Investments', 
                      vibe: 'The Strategist', 
                      strat: 'ROI masterclasses and off-plan analysis.', 
                      detail: 'Math-heavy content that kills the "fluff". High authority among technical investors who buy based on data over emotion.',
                      icon: TrendingUp, 
                      link: 'https://www.instagram.com/arman_dubai_investments' 
                    },
                    { 
                      name: 'Fabrizia Franceschetti', 
                      vibe: 'Lifestyle Expert', 
                      strat: 'European investor experience.', 
                      detail: 'Sells the "European Dream" in Dubai. Perfect for high-net-worth migration storytelling and aesthetic aspirational living.',
                      icon: Star, 
                      link: 'https://www.instagram.com/fabrizia_dxb' 
                    },
                    { 
                      name: 'Firas Al Msaddi', 
                      vibe: 'Market Authority', 
                      strat: 'CEO insights and data transparency.', 
                      detail: 'Macro-economic analysis. Builds trust through raw market transparency, government policy breakdowns, and high-level CEO perspective.',
                      icon: Briefcase, 
                      link: 'https://www.instagram.com/firas_al_msaddi' 
                    },
                    { 
                      name: 'Hussain Shihad', 
                      vibe: 'Viral Entertainer', 
                      strat: 'Humor to humanize interactions.', 
                      detail: 'Viral humor skits. Humanizes the industry and lowers the buyer\'s defensive wall by making agents relatable and approachable.',
                      icon: Video, 
                      link: 'https://www.instagram.com/dubai_real_estate_shihad' 
                    },
                    { 
                      name: 'Farooq Syed', 
                      vibe: 'Luxury Icon', 
                      strat: 'Sells the Dream. Trophy Assets.', 
                      detail: 'Status-driven luxury. Cinematic tours that sell "Trophy Assets" to investors looking for prestige and legacy acquisitions.',
                      icon: Camera, 
                      link: 'https://www.instagram.com/farooq_syd' 
                    },
                    { 
                      name: 'RE Blondies', 
                      vibe: 'Boots on Ground', 
                      strat: 'Construction reality proof.', 
                      detail: 'Proof-of-work content. Real site visits that verify developer handovers and quality benchmarks through raw, onsite footage.',
                      icon: Construction, 
                      link: 'https://www.instagram.com/realestateblondies' 
                    },
                    { 
                      name: 'Adilet Chynystanov', 
                      vibe: 'Cross-City Expert', 
                      strat: 'Rail and infrastructure focus.', 
                      detail: 'Infrastructure narrative. Links city growth to ROI through logical mapping of Metro Blue Line and Etihad Rail stations.',
                      icon: MapPin, 
                      link: 'https://www.instagram.com/adel_chynystanov_' 
                    },
                    { 
                      name: 'Paul Sharland', 
                      vibe: 'Authentic Local', 
                      strat: 'Walk-to-metro life vlogs.', 
                      detail: 'The community neighbor. Low-fi vlogs showing walkability, daily convenience, and the real life experience of Dubai residents.',
                      icon: Smartphone, 
                      link: 'https://www.instagram.com/paulsharland_investment' 
                    },
                    { 
                      name: 'Aeda Ayyad', 
                      vibe: 'Niche Specialist', 
                      strat: 'Expo City & Dubai South authority.', 
                      detail: 'Niche authority. Specialized knowledge in Expo City and developing growth corridors where future demand is being manufactured.',
                      icon: Award, 
                      link: 'https://www.instagram.com/aedaffayyad' 
                    },
                    { 
                      name: 'Myriam Laarif', 
                      vibe: 'Financial Advisor', 
                      strat: 'Property as a financial instrument.', 
                      detail: 'Asset management approach. Treats properties like stocks for the financial elite, focusing strictly on net yield and portolio scaling.',
                      icon: BarChart3, 
                      link: 'https://www.instagram.com/myriam_realtor2.0' 
                    },
                  ].map((user, i) => (
                    <div key={i} onClick={() => user.link && window.open(user.link, '_blank')} className="bg-[#e6c07b]/[0.02] border border-[#e6c07b]/10 p-8 rounded-[2rem] hover:border-[#e6c07b]/50 transition-all group relative cursor-pointer backdrop-blur-sm shadow-xl flex flex-col gap-4">
                       <div className="flex items-center justify-between">
                         <div className="w-12 h-12 bg-[#e6c07b]/10 rounded-2xl flex items-center justify-center group-hover:bg-[#e6c07b]/20 transition-colors">
                           <user.icon size={24} className="text-[#e6c07b]" />
                         </div>
                         <ExternalLink size={14} className="text-[#e6c07b] opacity-0 group-hover:opacity-100 transition-opacity" />
                       </div>
                       <div>
                         <h4 className="font-black text-lg uppercase mb-1 tracking-tight text-white">{user.name}</h4>
                         <p className="text-[10px] text-[#e6c07b] font-bold uppercase mb-3 tracking-widest">{user.vibe}</p>
                         <p className="text-xs text-gray-300 font-bold mb-2 italic">"{user.strat}"</p>
                         <div className="h-px w-full bg-[#e6c07b]/10 my-3" />
                         <p className="text-[11px] text-gray-500 leading-relaxed italic font-medium">{user.detail}</p>
                       </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-10">
                <div className="flex items-center gap-6">
                   <div className="w-16 h-16 bg-[#e6c07b]/10 border border-[#e6c07b]/30 rounded-full flex items-center justify-center">
                     <Users2 className="text-[#e6c07b]" size={32} />
                   </div>
                   <div>
                     <h3 className="text-5xl font-black uppercase italic tracking-tighter text-white">Category B: Developers</h3>
                     <p className="text-[#e6c07b] text-[11px] uppercase tracking-[0.4em] font-mono font-black">The Brand Giants selling "Civilizations"</p>
                   </div>
                </div>
                <div className="grid grid-cols-1 gap-8">
                  {[
                    { name: 'Emaar Properties (@emaardubai)', content: '"Community Maturity." High-quality UGC of happy families.', link: 'https://www.instagram.com/emaardubai' },
                    { name: 'Damac Properties (@damacofficial)', content: '"Tech-Luxury & Speed." Resort living and smart homes.', link: 'https://www.instagram.com/damacofficial' },
                    { name: 'Sobha Realty (@sobharealty)', content: '"The Art of Detail." Backward integration and forest living.', link: 'https://www.instagram.com/sobharealty' },
                    { name: 'Danube Properties (@danubeproperties)', content: '"Affordability & Urgency." 1% Payment plans and owner focus.', link: 'https://www.instagram.com/danubeproperties' },
                    { name: 'Nakheel (@nakheelofficial)', content: '"Mega-Scale Engineering." Legacy, scale, and coastal history.', link: 'https://www.instagram.com/nakheelofficial' }
                  ].map((dev, i) => (
                    <div key={i} onClick={() => window.open(dev.link, '_blank')} className="bg-[#e6c07b]/[0.03] border border-[#e6c07b]/10 p-10 rounded-[3rem] backdrop-blur-sm relative group overflow-hidden cursor-pointer hover:border-[#e6c07b]/50 transition-all shadow-2xl">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                        <div className="flex-1">
                          <h4 className="text-3xl font-black uppercase italic text-[#e6c07b]">{dev.name}</h4>
                          <p className="text-xs text-white/90 leading-relaxed font-medium italic mt-2">{dev.content}</p>
                        </div>
                        <Building2 className="text-[#e6c07b]" size={32} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'deliverables' && (
            <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 space-y-16 pb-24">
              <div className="relative">
                <h3 className="text-4xl font-black italic text-[#e6c07b] uppercase mb-4 tracking-tighter">Strategic Deliverables</h3>
                <p className="text-gray-400 text-lg max-w-3xl leading-relaxed mb-12 font-medium">
                   Optimized content categories for the 2026 Dubai market maturity.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Fun Content */}
                <div className="bg-[#e6c07b]/[0.03] border border-[#e6c07b]/20 p-10 rounded-[3rem] backdrop-blur-sm relative group overflow-hidden shadow-2xl">
                  <div className="absolute -right-4 -top-4 opacity-5 rotate-12 group-hover:scale-110 transition-transform duration-500">
                    <Clapperboard size={150} color={colors.gold} />
                  </div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-3 bg-[#e6c07b]/20 rounded-2xl border border-[#e6c07b]/30">
                        <Clapperboard className="text-[#e6c07b]" size={24} />
                      </div>
                      <h4 className="text-2xl font-black uppercase italic tracking-tight text-[#e6c07b]">Fun Content</h4>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-8 font-medium italic">
                      Humanizing the brand through humor. Skits about daily agent struggles, client interactions, or memes adapted to property logic.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                       {[
                         { name: "The Client Expectation Gap", link: "https://www.instagram.com/reel/DSzOc7cjWpJ/" },
                         { name: "The Real Broker Life", link: "https://www.instagram.com/reel/DRO2cvckTkT/" },
                         { name: "Closing The Commission", link: "https://www.instagram.com/reel/DLU7FTByWO9/" },
                         { name: "Dubai Property Satire", link: "https://www.instagram.com/reel/CwEw6_grAN7/" }
                       ].map((reel, i) => (
                         <button key={i} onClick={() => window.open(reel.link, '_blank')} className="flex items-center gap-3 p-3 bg-[#e6c07b]/5 border border-[#e6c07b]/20 rounded-xl hover:bg-[#e6c07b]/20 hover:border-[#e6c07b] transition-all">
                            <PlayCircle size={16} className="text-[#e6c07b]" />
                            <span className="text-[10px] font-black uppercase tracking-tight text-[#e6c07b]">{reel.name}</span>
                         </button>
                       ))}
                    </div>
                    <ul className="space-y-3">
                      {['Market Satire Reels', 'Agent VS Reality skits', 'Niche Humor skits'].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-xs font-bold text-white/70">
                          <CheckCircle2 size={14} className="text-[#e6c07b]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Educational Content */}
                <div className="bg-[#e6c07b]/[0.03] border border-[#e6c07b]/20 p-10 rounded-[3rem] backdrop-blur-sm relative group overflow-hidden shadow-2xl">
                  <div className="absolute -right-4 -top-4 opacity-5 rotate-12 group-hover:scale-110 transition-transform duration-500">
                    <BookOpen size={150} color={colors.gold} />
                  </div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-3 bg-[#e6c07b]/20 rounded-2xl border border-[#e6c07b]/30">
                        <BookOpen className="text-[#e6c07b]" size={24} />
                      </div>
                      <h4 className="text-2xl font-black uppercase italic tracking-tight text-[#e6c07b]">Educational</h4>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-8 font-medium italic">
                      Building authority through hard data. Deep dives into RERA Index, Golden Visa updates, and ROI masterclasses.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                       {[
                         { name: "Golden Visa 2026 Strategy", link: "https://www.instagram.com/reel/DPbTFVhDS8t/" },
                         { name: "RERA Smart Index Analysis", link: "https://www.instagram.com/reel/CrJChYtLi47/" },
                         { name: "ROI Calculation Breakdown", link: "https://www.instagram.com/reel/DTa2MBcEyFv/" },
                         { name: "Community Price Comparison", link: "https://www.instagram.com/reel/DTajJ2Yk7Pk/" }
                       ].map((reel, i) => (
                         <button key={i} onClick={() => window.open(reel.link, '_blank')} className="flex items-center gap-3 p-3 bg-[#e6c07b]/5 border border-[#e6c07b]/20 rounded-xl hover:bg-[#e6c07b]/20 hover:border-[#e6c07b] transition-all">
                            <PlayCircle size={16} className="text-[#e6c07b]" />
                            <span className="text-[10px] font-black uppercase tracking-tight text-[#e6c07b]">{reel.name}</span>
                         </button>
                       ))}
                    </div>
                    <ul className="space-y-3">
                      {['Net Yield Breakdowns', 'Legal Framework explainers', 'Area ROI Analysis'].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-xs font-bold text-white/70">
                          <CheckCircle2 size={14} className="text-[#e6c07b]" />
                          {item}
                        </li>
                      ))}
                      <li className="flex items-start gap-3 text-xs font-bold text-[#e6c07b] pt-2">
                        <CheckCircle2 size={14} className="text-[#e6c07b] mt-0.5" />
                        <span>Available as: Reels, Carousels, or detailed Static Posts</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Onsite Content */}
                <div className="bg-[#e6c07b]/[0.03] border border-[#e6c07b]/20 p-10 rounded-[3rem] backdrop-blur-sm relative group overflow-hidden shadow-2xl">
                  <div className="absolute -right-4 -top-4 opacity-5 rotate-12 group-hover:scale-110 transition-transform duration-500">
                    <Construction size={150} color={colors.gold} />
                  </div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-3 bg-[#e6c07b]/20 rounded-2xl border border-[#e6c07b]/30">
                        <Construction className="text-[#e6c07b]" size={24} />
                      </div>
                      <h4 className="text-2xl font-black uppercase italic tracking-tight text-[#e6c07b]">Onsite Content</h4>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed mb-8 font-medium italic">
                      "Boots on the ground" verification through site visits, construction updates, and finishing checks.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                       {[
                         { name: "Ground Reality Site Tour", link: "https://www.instagram.com/reel/DQ6KAdVDGNz/" },
                         { name: "Investor Handover Audit", link: "https://www.instagram.com/reel/DQ3sRmrkeUR/" }
                       ].map((reel, i) => (
                         <button key={i} onClick={() => window.open(reel.link, '_blank')} className="flex items-center gap-3 p-3 bg-[#e6c07b]/5 border border-[#e6c07b]/20 rounded-xl hover:bg-[#e6c07b]/20 hover:border-[#e6c07b] transition-all">
                            <PlayCircle size={16} className="text-[#e6c07b]" />
                            <span className="text-[10px] font-black uppercase tracking-tight text-[#e6c07b]">{reel.name}</span>
                         </button>
                       ))}
                    </div>
                    <ul className="space-y-3">
                      {['Milestone updates', 'Hard-hat site tours', 'Finishing quality checks'].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-xs font-bold text-white/70">
                          <CheckCircle2 size={14} className="text-[#e6c07b]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Microformat Content */}
                <div className="bg-[#e6c07b]/[0.03] border border-[#e6c07b]/20 p-10 rounded-[3rem] backdrop-blur-sm relative group overflow-hidden shadow-2xl">
                  <div className="absolute -right-4 -top-4 opacity-5 rotate-12 group-hover:scale-110 transition-transform duration-500">
                    <Timer size={150} color={colors.gold} />
                  </div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-3 bg-[#e6c07b]/20 rounded-2xl border border-[#e6c07b]/30">
                        <Timer className="text-[#e6c07b]" size={24} />
                      </div>
                      <h4 className="text-2xl font-black uppercase italic tracking-tight text-[#e6c07b]">Microformat</h4>
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed mb-8 font-medium italic">
                      High-impact storytelling. 15-30 second reels focusing on single data points or payment plan hooks.
                    </p>
                    <ul className="space-y-3">
                      {['1% Payment Plan hooks', '30-sec ROI tips', 'Metro distance reveals'].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-xs font-bold text-[#e6c07b]">
                          <CheckCircle2 size={14} className="text-[#e6c07b]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 space-y-16 pb-24">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-[#e6c07b]/20 pb-12">
                <div className="max-w-xl">
                  <h3 className="text-5xl font-black italic text-[#e6c07b] uppercase mb-4 tracking-tighter">Dopameme Systems</h3>
                  <p className="text-gray-400 text-lg font-medium leading-relaxed">
                    A comprehensive, high-output partnership designed to scale authority and automate client acquisition.
                  </p>
                </div>
                <div className="bg-[#e6c07b] p-8 rounded-[2.5rem] border border-[#e6c07b] shadow-[0_20px_50px_rgba(230,192,123,0.3)] min-w-[320px]">
                  <div className="flex items-center gap-3 mb-2">
                    <Wallet size={20} className="text-black" />
                    <span className="text-black font-black uppercase text-[10px] tracking-widest">Pricing</span>
                  </div>
                  <div className="text-black text-4xl font-black italic tracking-tighter mb-1">45,000 AED</div>
                  <div className="text-black/80 font-bold uppercase text-xs tracking-widest border-b border-black/10 pb-4 mb-4">Total for 3 Months</div>
                  <div className="flex items-center gap-3 text-black/90 font-black italic text-sm">
                    <Camera size={18} />
                    <span>2 Shoot Days / Month</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { title: "Notion Dashboard", desc: "Dedicated Notion workspace for project transparency and tracking.", icon: Layout },
                  { title: "World-Class Editing", desc: "Access to elite video editors specialized in short-form virality.", icon: Scissors },
                  { title: "In-Depth Research", desc: "Market analysis and ROI data used to build authority content.", icon: Search },
                  { title: "Graphics Team", desc: "Custom-made, high-end visual assets for static and carousel posts.", icon: Image },
                  { title: "Automation Setup", desc: "Custom DM and comment automation bots to capture interest 24/7.", icon: Bot },
                  { title: "Success Manager", desc: "Dedicated 24/7 support to manage workflows and milestones.", icon: Headphones },
                  { title: "Story Funnels", desc: "Strategic story sequences designed to convert viewers into clients.", icon: Filter },
                  { title: "AI-Assisted Workflow", desc: "Optimized content pipelines using advanced AI toolsets.", icon: Cpu },
                  { title: "Asset Magnets", desc: "Creation and setup of data collection tools and PDF assets.", icon: Magnet },
                  { title: "Data Collection", desc: "Email capture and CRM synchronization for direct marketing.", icon: Mail },
                  { title: "Cross-Posting", desc: "Multi-platform distribution support for maximal reach.", icon: Share2 }
                ].map((service, i) => (
                  <div key={i} className="bg-[#e6c07b]/[0.03] border border-[#e6c07b]/10 p-8 rounded-[2rem] hover:border-[#e6c07b]/40 transition-all group shadow-xl backdrop-blur-sm">
                    <div className="w-12 h-12 bg-[#e6c07b]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#e6c07b]/20 transition-colors">
                      <service.icon size={22} className="text-[#e6c07b]" />
                    </div>
                    <h4 className="text-[#e6c07b] font-black uppercase text-sm mb-2 tracking-tight">{service.title}</h4>
                    <p className="text-gray-500 text-xs leading-relaxed italic font-medium">{service.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default App;
